import { useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import _mapValues from 'lodash/mapValues';

import { getIncomes, isIncomeCountrySpecific } from '@app/participant-fields';

import { Theme } from '@app/theme';
import {
  TooltipText,
  Input,
  SubmitButton,
  CreatableTagsAutocomplete,
  DateInput,
  Select,
  SubmitForm,
  CountryKeySelect,
  PhoneNumberInput,
  ConsentCheckbox,
} from 'components';
import { Participant } from '@app/api';
import {
  createParticipantsInPanel,
  panelSubscriptionSettingsByIdSelector,
  getSubscriptionFormSettings,
  ParticipantSource,
  ParticipantTag,
  Gender,
  EducationLevel,
  FamilyStatus,
  CompanySize,
  Income,
  HierarchicalLevel,
  CompanyType,
} from 'store';

import { useFetchedEntity } from 'hooks/useFetched';
import { SubscriptionFormBg } from './SubscriptionFormBg';
import { YupValidators } from 'lib/validators';
import { clientSpecialConfigs } from 'config/clientSpecialConfigs';
import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { useParticipantFieldsByName, Consent, TechSavviness } from '@app/api';
import { FormikConsumer } from 'formik';

const useStyles = createUseStyles((theme: Theme) => ({
  dataInfo: {
    ...theme.fonts.body,
    color: theme.colors.blueAccessible,
  },
  form: {
    maxWidth: theme.maxContentWidth.reducedFormContent,
    margin: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.marginBase * 7,
  },
}));

const defaultFields = ['firstName', 'lastName', 'email', 'phoneNumber'];

type Values = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  consents: Consent[];
  tags: ParticipantTag[];
  country?: string;
  city: string;
  postalCode: string;
  gender: Gender | '' | null;
  jobTitle: string;
  birthdate: string;

  educationLevel?: EducationLevel;
  familyStatus?: FamilyStatus;
  techSavviness?: TechSavviness;
  hierarchicalLevel?: HierarchicalLevel;
  industries?: string;
  companySize?: CompanySize;
  companyType?: CompanyType;
  income?: Income;
};

const initialValues: Values = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  consents: [],
  tags: [],
  country: undefined,
  city: '',
  postalCode: '',
  gender: '',
  jobTitle: '',
  birthdate: '',
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required(),
  lastName: Yup.string(),
  email: Yup.string().required().email(),
  phoneNumber: YupValidators.isPhoneNumber(),
  consents: Yup.array().required().min(1),
  city: Yup.string(),
  postalCode: Yup.string(),
  gender: Yup.string(),
  jobTitle: Yup.string(),
  birthdate: YupValidators.isDateValid(),
});

export const PanelSubscriptionForm = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const fields = useParticipantFieldsByName();

  const settings = useSelector((state: any) =>
    panelSubscriptionSettingsByIdSelector(state, id),
  );
  const { fetching } = useFetchedEntity(getSubscriptionFormSettings, id);

  const thunk = useCallback(
    async (values: Values) => {
      if (!settings) {
        return;
      }
      return createParticipantsInPanel(
        {
          ..._mapValues(values, (val: any) =>
            val || val === 0 ? val : undefined,
          ),
          source: ParticipantSource.STANDALONE_FORM,
          tags: values.tags || [],
          birthdate: values.birthdate
            ? moment(values.birthdate).format()
            : undefined,
          locales: [i18n.language],
        },
        settings.panelId,
      );
    },
    [settings],
  );
  const fieldVisible = (field: keyof Participant | string): boolean =>
    !!settings && !!settings.fields
      ? settings.fields.includes(field)
      : defaultFields.includes(field);

  return (
    <AnalyticsCategoryProvider category={Categories.SUBSCRIBE_PANEL}>
      <SubscriptionFormBg
        fetching={fetching && !settings}
        logoUrl={settings ? settings.logoUrl : undefined}
        title={
          (settings && settings.title) || (
            <Trans i18nKey="panelSubscription.title" />
          )
        }
      >
        <SubmitForm
          submitCall={thunk}
          initialValues={initialValues}
          onSuccess={() => navigate('success')}
          validationSchema={validationSchema}
          className={classes.form}
        >
          <TooltipText title={t('panelSubscription.dataInfo.tooltip')}>
            <div className={classes.dataInfo}>
              <Trans i18nKey="panelSubscription.dataInfo.text" />
            </div>
          </TooltipText>

          <Input
            name="firstName"
            label={t('panelSubscription.participants.labels.firstName')}
            requiredStar
            placeholder={t(
              'panelSubscription.participants.placeholder.firstName',
            )}
          />
          {fieldVisible('lastName') && (
            <Input
              name="lastName"
              label={t('panelSubscription.participants.labels.lastName')}
              placeholder={t(
                'panelSubscription.participants.placeholder.lastName',
              )}
            />
          )}
          <Input
            name="email"
            label={t('panelSubscription.participants.labels.email')}
            requiredStar
            placeholder={t('panelSubscription.participants.placeholder.email')}
            type="email"
          />
          {fieldVisible('phoneNumber') && (
            <PhoneNumberInput
              name="phoneNumber"
              label={t('panelSubscription.participants.labels.phoneNumber')}
            />
          )}

          {!!settings && !!settings.tags && !!settings.tags.length && (
            <CreatableTagsAutocomplete
              label={t('panelSubscription.participants.labels.tags')}
              name="tags"
              isMulti
              options={settings.tags}
              creatable={false}
              placeholder={t('panelSubscription.participants.placeholder.tags')}
            />
          )}

          {fieldVisible('countryKey') && (
            <CountryKeySelect
              name="countryKey"
              noMargin
              label={t('panelSubscription.participants.labels.country')}
            />
          )}

          {fieldVisible('city') && (
            <Input
              name="city"
              label={t('panelSubscription.participants.labels.city')}
              placeholder={t('panelSubscription.participants.placeholder.city')}
            />
          )}
          {fieldVisible('postalCode') && (
            <Input
              name="postalCode"
              label={t('panelSubscription.participants.labels.postalCode')}
              placeholder={t(
                'panelSubscription.participants.placeholder.postalCode',
              )}
            />
          )}
          {fieldVisible('gender') && (
            <Select
              name="gender"
              label={t('panelSubscription.participants.labels.gender')}
              options={fields?.gender?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('birthdate') && (
            <DateInput
              id="birthdate"
              name="birthdate"
              label={t('panelSubscription.participants.labels.birthdate')}
            />
          )}
          {fieldVisible('devices') && (
            <Select
              name="devices"
              label={t('panelSubscription.participants.labels.devices')}
              options={fields?.devices?.choices || []}
              isClearable
              isMulti
            />
          )}

          {fieldVisible('educationLevel') && (
            <Select
              name="educationLevel"
              label={t('panelSubscription.participants.labels.educationLevel')}
              options={fields?.educationLevel?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('familyStatus') && (
            <Select
              name="familyStatus"
              label={t('panelSubscription.participants.labels.familyStatus')}
              options={fields?.familyStatus?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('techSavviness') && (
            <Select
              name="techSavviness"
              options={fields?.techSavviness?.choices || []}
              label={t('panelSubscription.participants.labels.techSavviness')}
              isClearable
            />
          )}
          {fieldVisible('jobTitle') && (
            <Input
              name="jobTitle"
              label={t('panelSubscription.participants.labels.jobTitle')}
              placeholder={t(
                'panelSubscription.participants.placeholder.jobTitle',
              )}
            />
          )}
          {fieldVisible('income') && (
            <FormikConsumer>
              {({ values }) =>
                values.countryKey &&
                isIncomeCountrySpecific([values.countryKey]) && (
                  <Select
                    name="income"
                    label={t('panelSubscription.participants.labels.income')}
                    options={getIncomes([values.countryKey])}
                    isClearable
                  />
                )
              }
            </FormikConsumer>
          )}
          {fieldVisible('professionalSituation') && (
            <Select
              name="professionalSituation"
              label={fields?.professionalSituation?.label}
              options={fields?.professionalSituation?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('hierarchicalLevel') && (
            <Select
              name="hierarchicalLevel"
              label={t(
                'panelSubscription.participants.labels.hierarchicalLevel',
              )}
              options={fields?.hierarchicalLevel?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('industries') && (
            <Select
              name="industries"
              label={t('panelSubscription.participants.labels.industry')}
              options={fields?.industries?.choices || []}
              isClearable
              isMulti
            />
          )}
          {fieldVisible('companySize') && (
            <Select
              name="companySize"
              label={t('panelSubscription.participants.labels.companySize')}
              options={fields?.companySize?.choices || []}
              isClearable
            />
          )}
          {fieldVisible('companyType') && (
            <Select
              name="companyType"
              label={t('panelSubscription.participants.labels.companyType')}
              options={fields?.companyType?.choices || []}
              isClearable
            />
          )}

          {clientSpecialConfigs.map(
            (config: any) =>
              settings &&
              settings.panelId === config.panelId &&
              config.specialFields.map(
                (Field: any) =>
                  fieldVisible(Field.name) && (
                    <Field.formComponent
                      name={Field.name}
                      label={Field.participantLabel}
                    />
                  ),
              ),
          )}
          <ConsentCheckbox name="consents" consent="PARTICIPANT_SIGNUP" />
          <div className={classes.buttonContainer}>
            <SubmitButton>
              <Trans i18nKey="panelSubscription.submit" />
            </SubmitButton>
          </div>
        </SubmitForm>
      </SubscriptionFormBg>
    </AnalyticsCategoryProvider>
  );
};
